import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import _ from 'lodash';
import PropTypes from 'prop-types';
import {
  Badge,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Stack,
  CircularProgress,
  Alert,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import axios from 'axios';
import api from '../../utils/functions/api';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { preprocessDocumentImage } from '../../utils/imagePreprocess';
import * as actions from '../../redux/actions';
import { EmployeeForm } from '../../components/Forms';
import { BackButton } from '../../components/Button';

const DOC_CATEGORIES = [
  { key: 'id_card', label: 'บัตรประชาชน' },
  { key: 'house_registration', label: 'ทะเบียนบ้าน' },
  { key: 'education', label: 'วุฒิการศึกษา' },
  { key: 'resume', label: 'Resume / CV' },
  { key: 'certificate', label: 'ใบรับรอง / ใบอบรม' },
  { key: 'other', label: 'เอกสารอื่นๆ' },
];

function PendingDocCard({ dataUrl, label, onDelete }) {
  const isImage = dataUrl?.startsWith('data:image');
  return (
    <Box
      sx={{
        position: 'relative',
        width: 120,
        height: 90,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid #ddd',
        flexShrink: 0,
      }}
    >
      {isImage ? (
        <img src={dataUrl} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f5f5f5',
            gap: 0.5,
          }}
        >
          <InsertDriveFileIcon color="action" />
          <Typography variant="caption" sx={{ px: 0.5, textAlign: 'center', wordBreak: 'break-all', lineHeight: 1.2 }}>
            {label}
          </Typography>
        </Box>
      )}
      {onDelete && (
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
            bgcolor: 'rgba(255,255,255,0.85)',
            '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
            p: 0.3,
          }}
          onClick={onDelete}
        >
          <DeleteIcon fontSize="small" color="error" />
        </IconButton>
      )}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'rgba(0,0,0,0.5)',
          px: 0.5,
          py: 0.2,
        }}
      >
        <Typography variant="caption" color="white" noWrap display="block">
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

PendingDocCard.propTypes = {
  dataUrl: PropTypes.string.isRequired,
  label: PropTypes.string,
  onDelete: PropTypes.func,
};

PendingDocCard.defaultProps = {
  label: '',
  onDelete: null,
};

// Thai ID-1: 85.6mm × 54mm at 300 DPI → 1011×638 px (85.6×300/25.4 = 1011.02)
const ID_TW = 1011;
const ID_TH = 638;
const ID_TR = ID_TW / ID_TH; // ≈ 1.5846

function cropToIdCard(dataUrl, bbox) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const W = img.naturalWidth;
      const H = img.naturalHeight;
      let sx; let sy; let sw; let sh;

      const validBbox = Array.isArray(bbox) && bbox.length === 4
        && bbox.every((v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1)
        && bbox[2] > bbox[0] && bbox[3] > bbox[1];

      if (validBbox) {
        const [x0, y0, x1, y1] = bbox;
        sx = x0 * W; sy = y0 * H;
        sw = (x1 - x0) * W; sh = (y1 - y0) * H;
        // Expand shorter dimension to match ID card ratio (keep center)
        const r = sw / sh;
        if (r > ID_TR) {
          const newH = sw / ID_TR;
          sy -= (newH - sh) / 2;
          sh = newH;
        } else {
          const newW = sh * ID_TR;
          sx -= (newW - sw) / 2;
          sw = newW;
        }
      } else {
        // No bbox — center crop to ID card ratio
        const r = W / H;
        if (r > ID_TR) {
          sw = H * ID_TR; sh = H; sx = (W - sw) / 2; sy = 0;
        } else {
          sw = W; sh = W / ID_TR; sx = 0; sy = (H - sh) / 2;
        }
      }

      // Slide before cut: shift viewport inward before truncating
      if (sx < 0) sx = 0;
      if (sy < 0) sy = 0;
      if (sx + sw > W) sx = Math.max(0, W - sw);
      if (sy + sh > H) sy = Math.max(0, H - sh);
      // Final clamp in case image is smaller than expanded region
      sw = Math.min(sw, W - sx);
      sh = Math.min(sh, H - sy);

      const canvas = document.createElement('canvas');
      canvas.width = ID_TW; canvas.height = ID_TH;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, ID_TW, ID_TH);
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, ID_TW, ID_TH);
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    img.src = dataUrl;
  });
}

const defaultValue = {
  firstname: '',
  lastname: '',
  department: '',
  role: '',
  username: '',
  password: '',
  phone_number: '',
};

export default function HrAddEmployee({ title, subtitle }) {
  const dispatch = useDispatch();
  const employee = useSelector((state) => state.employee);
  const roletype = useSelector((state) => state.roletype);
  const department = useSelector((state) => state.department);
  const history = useHistory();

  const [addUser, setAddUser] = useState(false);
  const [addedEmployeeImage, setAddedEmployeeImage] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [pendingIdCardImage, setPendingIdCardImage] = useState(null); // cropped dataUrl
  const [pendingDocs, setPendingDocs] = useState([]);
  const docFileRefs = useRef({});

  // --- ID Card Scanner state ---
  const [openIdScan, setOpenIdScan] = useState(false);
  const [idScanImage, setIdScanImage] = useState(null);
  const [idScanMime, setIdScanMime] = useState('image/jpeg');
  const [idScanPreviewUrl, setIdScanPreviewUrl] = useState(null);
  const [idScanLoading, setIdScanLoading] = useState(false);
  const [idScanResult, setIdScanResult] = useState(null);
  const [idScanError, setIdScanError] = useState('');
  const [idScanDuplicates, setIdScanDuplicates] = useState([]);
  const [idScanDupLoading, setIdScanDupLoading] = useState(false);
  const idFileRef = useRef();

  const runDuplicateCheck = async (result) => {
    if (!result) return;
    setIdScanDupLoading(true);
    setIdScanDuplicates([]);
    try {
      const terms = _.uniq([result.tax_id, result.firstname_th, result.firstname].filter(Boolean));
      const found = [];
      for (const term of terms) {
        const { data } = await api.get(
          `${process.env.REACT_APP_API_URL}/employee?name=${encodeURIComponent(term)}&size=20&page=1&activeOnly=false`,
        );
        (data?.rows || []).forEach((r) => {
          const taxMatch = result.tax_id && r.tax_id === result.tax_id;
          const nameThMatch = result.firstname_th && result.lastname_th
            && r.firstname_th === result.firstname_th && r.lastname_th === result.lastname_th;
          const nameEnMatch = result.firstname && result.lastname
            && r.firstname?.toLowerCase() === result.firstname?.toLowerCase()
            && r.lastname?.toLowerCase() === result.lastname?.toLowerCase();
          if ((taxMatch || nameThMatch || nameEnMatch) && !found.find((f) => f.employee._id === r._id)) {
            found.push({ employee: r, reason: taxMatch ? 'เลขบัตรประชาชนซ้ำ' : 'ชื่อ-นามสกุลซ้ำ' });
          }
        });
      }
      setIdScanDuplicates(found);
    } catch (err) {
      console.error('Duplicate check failed:', err);
    } finally {
      setIdScanDupLoading(false);
    }
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm();

  useEffect(() => {
    dispatch(actions.roletypeAll(''));
    dispatch(actions.departmentAll({}));
    return () => {};
  }, []);

  const handleIdImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIdScanResult(null);
    setIdScanError('');
    setIdScanMime(file.type || 'image/jpeg');
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setIdScanPreviewUrl(dataUrl);
      setIdScanImage(dataUrl.split(',')[1]);
    };
    reader.readAsDataURL(file);
  };

  const handleIdScanAnalyze = async () => {
    if (!idScanImage) return;
    setIdScanLoading(true);
    setIdScanError('');
    setIdScanResult(null);
    try {
      const base64 = await preprocessDocumentImage(`data:${idScanMime};base64,${idScanImage}`);
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/ai/scan-id-card`,
        { image: base64, mimeType: 'image/jpeg' },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const result = data?.result ?? data;
      setIdScanResult(result);
      await runDuplicateCheck(result);
    } catch (err) {
      setIdScanError(err?.response?.data?.error?.message || 'วิเคราะห์ไม่สำเร็จ กรุณาลองใหม่');
    } finally {
      setIdScanLoading(false);
    }
  };

  const handleIdScanFill = async () => {
    if (!idScanResult) return;
    const r = idScanResult;
    if (r.title) setValue('title', r.title);
    if (r.firstname) setValue('firstname', r.firstname);
    if (r.lastname) setValue('lastname', r.lastname);
    if (r.firstname_th) setValue('firstname_th', r.firstname_th);
    if (r.lastname_th) setValue('lastname_th', r.lastname_th);
    if (r.tax_id) setValue('tax_id', r.tax_id);
    if (r.date_of_birth) setValue('date_of_birth', r.date_of_birth);
    if (r.gender) setValue('gender', r.gender);
    if (r.religion) setValue('religion', r.religion);
    else setValue('religion', 'ไม่ระบุ');
    if (r.address) {
      if (r.address.house_number) setValue('address.house_number', r.address.house_number);
      if (r.address.village_number) setValue('address.village_number', r.address.village_number);
      if (r.address.road) setValue('address.road', r.address.road);
      if (r.address.subdistrict) setValue('address.subdistrict', r.address.subdistrict);
      if (r.address.district) setValue('address.district', r.address.district);
      if (r.address.province) setValue('address.province', r.address.province);
    }
    // crop ID card image and keep for submission
    if (idScanPreviewUrl) {
      const cropped = await cropToIdCard(idScanPreviewUrl, r.card_bbox);
      setPendingIdCardImage(cropped);
    }
    setOpenIdScan(false);
    setIdScanImage(null);
    setIdScanPreviewUrl(null);
    setIdScanResult(null);
    setIdScanError('');
  };

  const handleDocAdd = (category, label, file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setPendingDocs((prev) => [
        ...prev,
        { id: `${Date.now()}-${Math.random()}`, category, label, dataUrl: e.target.result },
      ]);
    };
    reader.readAsDataURL(file);
  };

  const handleDocRemove = (docId) => {
    setPendingDocs((prev) => prev.filter((d) => d.id !== docId));
  };

  const renderDocumentsTab = () => (
    <Stack spacing={3} sx={{ pt: 2 }}>
      <Alert severity="info" sx={{ mb: 1 }}>
        เอกสารที่เพิ่มไว้จะถูกบันทึกพร้อมกับข้อมูลพนักงานเมื่อกด "บันทึกข้อมูล"
      </Alert>
      {DOC_CATEGORIES.map(({ key, label }) => {
        const catDocs = pendingDocs.filter((d) => d.category === key);
        const isIdCard = key === 'id_card';
        return (
          <Box key={key}>
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
              {label}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
              {isIdCard && pendingIdCardImage && (
                <PendingDocCard dataUrl={pendingIdCardImage} label="บัตรประชาชน (AI scan)" />
              )}
              {catDocs.map((doc) => (
                <PendingDocCard
                  key={doc.id}
                  dataUrl={doc.dataUrl}
                  label={doc.label}
                  onDelete={() => handleDocRemove(doc.id)}
                />
              ))}
              <Box
                sx={{
                  width: 120,
                  height: 90,
                  border: '2px dashed #ccc',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  gap: 0.5,
                  color: 'text.secondary',
                  '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                }}
                onClick={() => docFileRefs.current[key]?.click()}
              >
                <UploadFileIcon fontSize="small" />
                <Typography variant="caption">เพิ่มเอกสาร</Typography>
              </Box>
              <input
                ref={(el) => { docFileRefs.current[key] = el; }}
                type="file"
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleDocAdd(key, file.name, file);
                  e.target.value = '';
                }}
              />
            </Box>
            <Divider sx={{ mt: 2 }} />
          </Box>
        );
      })}
    </Stack>
  );

  const renderIdScanDialog = () => (
    <Dialog open={openIdScan} onClose={() => setOpenIdScan(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <BadgeIcon color="primary" />
          <span>สแกนบัตรประชาชน</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <input
            ref={idFileRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleIdImageChange}
          />
          <Button variant="outlined" fullWidth onClick={() => idFileRef.current?.click()}>
            เลือกรูปภาพ / ถ่ายรูปบัตร
          </Button>
          {idScanPreviewUrl && (
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={idScanPreviewUrl}
                alt="id-card-preview"
                style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 8, border: '1px solid #ddd' }}
              />
            </Box>
          )}
          {idScanError && <Alert severity="error">{idScanError}</Alert>}
          {idScanResult && (
            <Alert severity="success">
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>ข้อมูลที่อ่านได้</Typography>
              {idScanResult.title && <div>คำนำหน้า: <b>{idScanResult.title}</b></div>}
              {idScanResult.firstname_th && <div>ชื่อ (TH): <b>{idScanResult.firstname_th} {idScanResult.lastname_th}</b></div>}
              {idScanResult.firstname && <div>ชื่อ (EN): <b>{idScanResult.firstname} {idScanResult.lastname}</b></div>}
              {idScanResult.tax_id && <div>เลขบัตร: <b>{idScanResult.tax_id}</b></div>}
              {idScanResult.date_of_birth && <div>วันเกิด: <b>{idScanResult.date_of_birth}</b></div>}
              {idScanResult.gender && <div>เพศ: <b>{idScanResult.gender}</b></div>}
              {idScanResult.religion && <div>ศาสนา: <b>{idScanResult.religion}</b></div>}
              {idScanResult.address?.province && <div>จังหวัด: <b>{idScanResult.address.province}</b></div>}
            </Alert>
          )}
          {idScanDupLoading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">กำลังตรวจสอบข้อมูลซ้ำในระบบ...</Typography>
            </Stack>
          )}
          {idScanDuplicates.length > 0 && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                ⚠️ พบข้อมูลที่อาจซ้ำกันในระบบ ({idScanDuplicates.length} รายการ)
              </Typography>
              {idScanDuplicates.map((d, i) => (
                <div key={i}>
                  <b>{d.reason}</b>: {d.employee.firstname} {d.employee.lastname}
                  {d.employee.firstname_th && !/^-+$/.test(String(d.employee.firstname_th).trim())
                    && ` (${d.employee.firstname_th} ${d.employee.lastname_th})`}
                  {d.employee.employee_id && ` — ${d.employee.employee_id}`}
                </div>
              ))}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {idScanResult ? (
          <Button variant="contained" onClick={handleIdScanFill}>กรอกข้อมูลลงฟอร์ม</Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleIdScanAnalyze}
            disabled={!idScanImage || idScanLoading}
            startIcon={idScanLoading ? <CircularProgress size={16} color="inherit" /> : <BadgeIcon />}
          >
            {idScanLoading ? 'กำลังวิเคราะห์...' : 'วิเคราะห์'}
          </Button>
        )}
        <Button onClick={() => { setOpenIdScan(false); setIdScanImage(null); setIdScanPreviewUrl(null); setIdScanResult(null); setIdScanError(''); }}>
          ปิด
        </Button>
      </DialogActions>
    </Dialog>
  );

  const onSubmit = async (data) => {
    try {
      const confirm = await confirmAsync('คุณต้องการบันทึกข้อมูลนี้ใช่หรือไม่?');
      if (!confirm) return;

      const payload = {
        ...data,
        salary: {
          month: data?.salary?.month,
          day: data?.salary?.month / 30,
          hr: data?.salary?.month / 240,
        },
      };

      if (!_.isEmpty(addedEmployeeImage)) {
        payload.image = {
          image: addedEmployeeImage[0]?.data_url,
          imageType: 'profile',
          alt: '',
        };
      }

      let newId;
      if (addUser === true) {
        if (data.password !== data.confirmPassword) {
          showAlert('กรุณาใส่รหัสผ่านให้ตรงกัน');
          return;
        }
        await dispatch(actions.userRegister(payload));
      } else {
        // Direct API call to get the created employee's ID
        const { data: created } = await api.post(`${process.env.REACT_APP_API_URL}/employee`, payload);
        newId = created?._id || created?.id;

        if (newId) {
          // Upload id_card_image if pending
          if (pendingIdCardImage) {
            axios.put(
              `${process.env.REACT_APP_API_URL}/employee/${newId}`,
              { id_card_image: { image: pendingIdCardImage, imageType: 'profile', alt: 'id_card' } },
              { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
            ).catch((e) => console.error('id_card_image upload failed', e));
          }
          // Upload pending documents
          for (const doc of pendingDocs) {
            axios.post(
              `${process.env.REACT_APP_API_URL}/employee/${newId}/documents`,
              { category: doc.category, label: doc.label, image: doc.dataUrl },
              { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
            ).catch((e) => console.error('document upload failed', e));
          }
        }
      }

      reset(defaultValue);
      setAddUser(false);
      setAddedEmployeeImage([]);
      setPendingIdCardImage(null);
      setPendingDocs([]);

      if (newId) {
        history.push(`/humen/employee/edit/${newId}`);
      } else {
        history.goBack();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit(onSubmit)}>
      {pendingIdCardImage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          สแกนบัตรประชาชนแล้ว — รูปจะถูกบันทึกพร้อมข้อมูลพนักงาน
        </Alert>
      )}
      <EmployeeForm
        control={control}
        Controller={Controller}
        errors={errors}
        employee={employee}
        roletype={roletype}
        addUser={addUser}
        setAddUser={setAddUser}
        employeeImage={addedEmployeeImage}
        setEmployeeImage={setAddedEmployeeImage}
        department={department}
        watch={watch}
        setValue={setValue}
      />
      <Box sx={{ textAlign: 'center', p: 1 }}>
        <Button variant="contained" color="primary" type="submit" startIcon={<Badge />} size="large">
          บันทึกข้อมูล
        </Button>
      </Box>
    </form>
  );

  return (
    <div>
      {renderIdScanDialog()}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, pb: 2 }}>
        <BackButton />
        <Button
          variant="outlined"
          color="primary"
          startIcon={<BadgeIcon />}
          onClick={() => { setOpenIdScan(true); setIdScanImage(null); setIdScanPreviewUrl(null); setIdScanResult(null); setIdScanError(''); }}
        >
          สแกนบัตรประชาชน
        </Button>
      </Box>
      <Card sx={{ p: 3 }}>
        <Box sx={{ bgcolor: 'grey.100', mx: -3, px: 3, pt: 1, mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            TabIndicatorProps={{ style: { height: 3, borderRadius: '3px 3px 0 0' } }}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
                minHeight: 44,
                borderRadius: '6px 6px 0 0',
                color: 'text.secondary',
                mr: 0.5,
                '&.Mui-selected': { color: 'primary.main', fontWeight: 700, bgcolor: 'background.paper' },
              },
            }}
          >
            <Tab label="ข้อมูลทั่วไป" />
            <Tab
              label={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <span>เอกสาร</span>
                  {(pendingDocs.length > 0 || pendingIdCardImage) && (
                    <Chip
                      label={pendingDocs.length + (pendingIdCardImage ? 1 : 0)}
                      size="small"
                      color="warning"
                      sx={{ height: 18, fontSize: 10 }}
                    />
                  )}
                </Stack>
              }
            />
          </Tabs>
        </Box>

        {activeTab === 0 && renderForm()}

        {activeTab === 1 && renderDocumentsTab()}
      </Card>
    </div>
  );
}

HrAddEmployee.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

HrAddEmployee.defaultProps = {
  title: '',
  subtitle: '',
};
